import {useDispatch, useSelector} from 'react-redux';
import {Interceptor} from '@jobseeker/common-core/components/Inteceptor';
import useValidateAuth from '@jobseeker/common-core/hooks/useValidateAuth';
import {NavLayout, ResponsiveLayout, SeoLayout} from '@layouts';
import {getConfigByApp, getMonth, utilServerSideDeviceDetection} from '@utils';
import {metaInfo} from '@configs';
import {HOME, JOBFEED, RECRUITER_VIEW} from '@constants';
import type {GetServerSidePropsContext} from 'next/types';
import loadSEO from '@helpers/loadSEO';
import {wrapper} from '@jobseeker/common-core/stores';
import {VersionVariants} from '@jobseeker/lotus/enums';

import type {AppState} from '@jobseeker/common-core/types';
import {Box, Grid, Skeleton, Typography} from '@mui/material';
import {
  LabeledValue,
  SubSectionHeading,
  LabelValueList,
  FieldEmptyState,
  Avatar,
  Image,
} from '@jobseeker/lotus/components';
import {Tooltip} from '@jobseeker/lotus/elements';
import {Section} from '@jobseeker/lotus/components';
import {Download} from '@jobseeker/lotus/assets/icons';
import React, {useEffect, useState} from 'react';
import {isCSR, formatSalaryInLacs} from '@jobseeker/lotus/utils';
import {SeoIntrup} from '@layouts/SeoLayout';
import {openUrl, TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';
import HomePageFooter from '@components/HomeV2/homePageFooter';

type RecruiterViewProps = {
  seoConfig: object;
  metaData: /* eslint-disable @typescript-eslint/no-explicit-any */ any;
};

const RecruiterViewPage = ({seoConfig, metaData}: RecruiterViewProps) => {
  const {profile, profileLoading} = useSelector(
    (state: AppState) => state.user
  );
  const [showAllLocations, setShowAllLocations] = useState(false);
  const preferredLocations =
    profile.locations?.preferred.map((l: any) => l.name).join(', ') ?? '';
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();

  useValidateAuth(dispatch, true);

  const redirectTo = (url: string) => () => openUrl(url);
  const openPersonalEdit = redirectTo('/registration/addPersonalDetails');
  const openEducationalEdit = redirectTo('/registration/addEducationalDetails');

  const openWorkExperienceEdit = redirectTo('/registration/addWorkExperience');

  // const openAdditionalEdit = redirectTo('/registration/addAdditionalDetails');

  const router = useRouter();
  useEffect(() => {
    TrackEventUBA('recruiterviewPageView', 'view', {
      ref: router.query.ref || '',
      pageName: 'Recruiter View Pages',
      sessionUsername: `${sessionStorage.getItem('userId')}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

  return (
    <Interceptor metaInfo={metaData}>
      <SeoLayout seoConfig={seoConfig} />
      <NavLayout>
        <ResponsiveLayout
          breadcrumbElements={[]}
          variant={VersionVariants.VERSION_TWO}
        >
          {isCSR() && (
            <SeoIntrup pageNo={0} nextPage={false} type={RECRUITER_VIEW} />
          )}
          <Section
            heading="Recruiter's View"
            subHeading="This is how your profile will look to the recruiter"
            endEdornment={
              <Box
                component={profile.resumeUrl?.length ? 'a' : 'div'}
                href={profile.resumeUrl?.length ? profile.resumeUrl : undefined}
                sx={{
                  display: 'flex',
                  justifyContent: {
                    md: 'end',
                    xxs: 'start',
                  },
                  color: profile.resumeUrl ? 'primary.main' : 'text.secondary',
                  alignItems: 'end',
                  mb: 2,
                  cursor: profile.resumeUrl ? 'pointer' : 'default',
                  textDecoration: 'none',
                }}
              >
                <Tooltip
                  placement="top"
                  arrow
                  title={
                    !profile.resumeUrl?.length ? (
                      <Box p={1} width={'200px'}>
                        <Typography
                          component={'span'}
                          fontSize={theme => theme.typography.pxToRem(12)}
                          fontWeight={theme =>
                            theme.typography.fontWeightMedium
                          }
                          color={'secondary.main'}
                          fontStyle={'italic'}
                        >
                          You have not uploaded your resume
                          <Typography
                            component={'span'}
                            fontSize={theme => theme.typography.pxToRem(12)}
                            fontWeight={theme =>
                              theme.typography.fontWeightMedium
                            }
                            sx={{cursor: 'pointer', ml: 0.5}}
                            color={'primary.main'}
                            onClick={openPersonalEdit}
                            fontStyle={'normal'}
                          >
                            {'(Upload)'}
                          </Typography>
                        </Typography>
                      </Box>
                    ) : (
                      ''
                    )
                  }
                >
                  <Box display={'flex'}>
                    <Image
                      src={Download}
                      color={'primary.main'}
                      alt="Download"
                      style={{
                        marginBottom: '1px',
                        marginRight: '4px',
                        filter: `brightness(${profile.resumeUrl ? 0 : 2.5})`,
                      }}
                    />
                    <Typography
                      fontSize={16}
                      fontWeight={500}
                      color={
                        profile.resumeUrl ? 'primary.main' : 'text.secondary'
                      }
                      textAlign={'center'}
                    >
                      Download Resume
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
            }
            sx={
              isAppIimjobs
                ? {}
                : {
                    mx: {xxs: 2, md: 'auto'},
                    mt: 3,
                    width: 'auto',
                  }
            }
          >
            <Grid item container>
              <Grid xxs={12} md={4} data-testid="personal-details">
                <SubSectionHeading
                  heading="Personal Details"
                  sx={{
                    borderRadius: 2,
                    borderBottomRightRadius: {
                      md: 0,
                    },
                    borderTopRightRadius: {
                      md: 0,
                    },
                  }}
                />
                <Box
                  ml={2}
                  mb={5}
                  mr={{xxs: 0, md: 8}}
                  maxWidth={{xxs: '100%', md: 300}}
                  data-testid="personal-details-section"
                >
                  <Box sx={{display: 'flex', mt: 3, alignItems: 'center'}}>
                    <Box
                      onClick={() =>
                        !profile.picUrl?.length && openPersonalEdit()
                      }
                    >
                      <Avatar
                        imgUrl={profile.picUrl ?? ''}
                        sx={{
                          img: {
                            borderRadius: '100%',
                          },
                          height: '70px',
                          width: '70px',
                          borderRadius: '100%',
                          padding: '5px',
                          border: 'none',
                          background: isAppIimjobs
                            ? 'rgba(145, 184, 121, 0.16)'
                            : 'rgba(240, 201, 150, 0.16)',
                          cursor: profile.picUrl ? 'unset' : 'pointer',
                        }}
                        showFallbackIcon={!Boolean(profile.picUrl?.length)}
                      />
                    </Box>
                    <Box ml={1}>
                      <Typography
                        component={'p'}
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{
                          wordWrap: 'anywhere',
                        }}
                      >
                        {profileLoading === false ? (
                          profile.name.length ? (
                            profile.name
                          ) : (
                            <Box
                              sx={{cursor: 'pointer'}}
                              onClick={openPersonalEdit}
                            >
                              <Typography
                                color={'primary.main'}
                                fontWeight={500}
                                fontSize={12}
                              >
                                Add Name
                              </Typography>
                            </Box>
                          )
                        ) : (
                          <Skeleton width={80} />
                        )}
                      </Typography>

                      <Typography
                        component={'p'}
                        variant="body2"
                        sx={{wordWrap: 'anywhere', overflowWrap: 'break-word'}}
                      >
                        {profileLoading === false ? (
                          profile.email
                        ) : (
                          <Skeleton width={140} />
                        )}
                      </Typography>
                      <Typography component={'p'} variant="body2">
                        {profileLoading === false ? (
                          profile.phone.length ? (
                            profile.phone?.replace(/^0+/, '')
                          ) : (
                            <Box
                              sx={{cursor: 'pointer'}}
                              onClick={openPersonalEdit}
                            >
                              <Typography
                                color={'primary.main'}
                                fontWeight={500}
                                fontSize={12}
                              >
                                Add Phone Number
                              </Typography>
                            </Box>
                          )
                        ) : (
                          <Skeleton width={120} />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <LabeledValue
                      loading={profileLoading}
                      title="Experience"
                      value={
                        Array.isArray(profile?.professionalInfo) &&
                        profile.professionalInfo.length > 0 &&
                        profile.experienceYear === 0 &&
                        profile.experienceMonth === 0
                          ? ''
                          : `${
                              profile.experienceYear > 30
                                ? '30+'
                                : profile.experienceYear
                            } ${
                              profile?.experienceYear == 1 ? 'Year ' : 'Years '
                            } ${profile.experienceMonth} ${
                              profile?.experienceMonth == 1
                                ? 'Month '
                                : 'Months '
                            }`
                      }
                      emptyState={
                        <FieldEmptyState onAddClick={openPersonalEdit} />
                      }
                    />
                    <LabeledValue
                      loading={profileLoading}
                      title="Notice Period"
                      value={
                        profile?.noticePeriod
                          ? profile?.noticePeriod == '7'
                            ? 'Immediately Available'
                            : profile?.noticePeriod +
                              (profile?.noticePeriod !== '1'
                                ? ' Months'
                                : ' Month')
                          : ''
                      }
                      emptyState={
                        <FieldEmptyState onAddClick={openPersonalEdit} />
                      }
                    />

                    <LabeledValue
                      loading={profileLoading}
                      mt={3}
                      title="Location"
                      value={profile.locations?.current.name ?? ''}
                      emptyState={
                        <FieldEmptyState onAddClick={openPersonalEdit} />
                      }
                    />

                    <LabeledValue
                      loading={profileLoading}
                      title="Preferred Location"
                      value={
                        showAllLocations
                          ? preferredLocations
                          : preferredLocations.includes(',') &&
                            preferredLocations.length > 50
                          ? preferredLocations
                              .slice(0, 70)
                              .split(', ')
                              .slice(0, -1)
                              .join(', ')
                          : preferredLocations
                      }
                      emptyState={
                        <FieldEmptyState onAddClick={openPersonalEdit} />
                      }
                    />
                    {preferredLocations.length > 50 && (
                      <Box
                        onClick={() => setShowAllLocations(prev => !prev)}
                        sx={{cursor: 'pointer'}}
                      >
                        <Typography
                          fontSize={12}
                          color={'primary'}
                          fontWeight={500}
                        >
                          {showAllLocations ? 'See less' : 'See all locations'}
                        </Typography>
                      </Box>
                    )}

                    <LabeledValue
                      loading={profileLoading}
                      mt={3}
                      title="Annual Current Salary"
                      value={
                        isAppIimjobs
                          ? profile.currentSalary >= 0
                            ? profile.confidential
                              ? 'Confidential'
                              : formatSalaryInLacs(profile.currentSalary)
                            : ''
                          : Number(profile.currentCTC) >= 0
                          ? profile.confidential
                            ? 'Confidential'
                            : profile.currentCTC + ' Lacs'
                          : ''
                      }
                      emptyState={
                        <FieldEmptyState onAddClick={openPersonalEdit} />
                      }
                    />

                    <LabeledValue
                      loading={profileLoading}
                      title="Expected Annual Salary"
                      value={
                        isAppIimjobs
                          ? profile?.expectedSalary >= 0
                            ? profile.confidential
                              ? 'Confidential'
                              : formatSalaryInLacs(profile?.expectedSalary) +
                                (profile.negotiable ? ' (Negotiable)' : '')
                            : ''
                          : Number(profile.expectedCTC) >= 0
                          ? profile.confidential
                            ? 'Confidential'
                            : profile.expectedCTC +
                              ' Lacs' +
                              (profile.negotiable ? ' (Negotiable)' : '')
                          : ''
                      }
                      emptyState={
                        <FieldEmptyState onAddClick={openPersonalEdit} />
                      }
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid
                xxs={12}
                md={5}
                sx={{wordWrap: 'anywhere', overflowWrap: 'break-word'}}
                data-testid="educational-details"
              >
                <SubSectionHeading
                  heading="Educational Background"
                  sx={{
                    borderRadius: {
                      xxs: 2,
                      md: 0,
                    },
                  }}
                />
                <Box
                  ml={2}
                  mb={5}
                  mt={2}
                  mr={{xxs: 0, md: 8}}
                  maxWidth={{xxs: '100%', md: 300}}
                  data-testid="educational-background-section"
                >
                  <LabelValueList
                    data={
                      profile.educationInfo?.info?.length &&
                      Object.keys(profile.educationInfo?.info[0]).length
                        ? profile.educationInfo?.info
                        : []
                    }
                    getLabel={item => item.institute ?? ''}
                    getValue={item => [
                      item.degree + ` (${item.courseType})` ?? '',
                      item?.batchFrom && item?.batchTo
                        ? item?.batchFrom + ' - ' + item?.batchTo
                        : '',
                    ]}
                    labeledValueProps={{
                      invert: true,
                      mb: 2,
                      loading: profileLoading,
                    }}
                    loading={profileLoading}
                    placeholderCount={4}
                    emptyState={
                      <FieldEmptyState onAddClick={openEducationalEdit} />
                    }
                  />
                </Box>
              </Grid>
              <Grid
                xxs={12}
                md={3}
                sx={{wordWrap: 'anywhere', overflowWrap: 'break-word'}}
                data-testid="work-experience"
              >
                <SubSectionHeading
                  heading="Work Experience"
                  sx={{
                    borderRadius: 2,
                    borderBottomLeftRadius: {
                      md: 0,
                    },
                    borderTopLeftRadius: {
                      md: 0,
                    },
                  }}
                />
                <Box ml={2} mb={5} mt={2} data-testid="work-experience-section">
                  <LabelValueList
                    data={
                      Array.isArray(profile.professionalInfo)
                        ? profile.professionalInfo
                        : ([] as any[])
                    }
                    getLabel={item => item.organization ?? ''}
                    getValue={item => [
                      item?.designation ?? '',
                      item?.fromYear && item?.toYear
                        ? `${
                            item?.fromMonth
                              ? getMonth(item?.fromMonth).slice(0, 3)
                              : ''
                          } ${item?.fromYear} - ${
                            item?.isCurrent
                              ? 'Present'
                              : `${
                                  item?.toMonth
                                    ? getMonth(item?.toMonth).slice(0, 3)
                                    : ''
                                } ${item?.toYear}`
                          }`
                        : '',
                    ]}
                    labeledValueProps={{
                      invert: true,
                      mb: 2,
                      loading: profileLoading,
                    }}
                    loading={profileLoading}
                    maxListItemBeforeShowmore={5}
                    name={'experience'}
                    placeholderCount={5}
                    emptyState={
                      profile.experienceStatus === 2 ? (
                        <FieldEmptyState text="I do not have any prior work experience" />
                      ) : (
                        <FieldEmptyState onAddClick={openWorkExperienceEdit} />
                      )
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Section>
          {!isAppIimjobs && (
            <Box
              sx={{
                marginBottom: '-64px',
              }}
            >
              <HomePageFooter isTransparent={true} />
            </Box>
          )}
        </ResponsiveLayout>
      </NavLayout>
    </Interceptor>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  store => async (context: GetServerSidePropsContext) => {
    const metaData = getConfigByApp(metaInfo, JOBFEED, true, RECRUITER_VIEW);

    store.dispatch({
      type: 'AppContext/set_current_page',
      payload: {page: RECRUITER_VIEW},
    });

    const {isMobile} = utilServerSideDeviceDetection(context);
    if (isMobile) {
      store.dispatch({
        type: 'AppContext/set_layout_device',
        payload: 'mobile',
      });
    }

    const seoConfig = await loadSEO(HOME);

    return {
      props: {
        seoConfig,
        metaData,
      },
    };
  }
);

export default RecruiterViewPage;
